import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, Bell, Search, MessageSquare, Calendar, CheckCircle, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { db } from '../../services/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

// Notification sound (simple ding)
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZN3pHwAAAAAAD/+1DEAAAGAAGn9AAAIwvAb/zwABMkCEIQhCGIAAAAADPmfMA8H4fi/EAQPg+8H3/+XB9/1Af/8H3//////wfd/gg+D4Pg++D58EAQBA+D4IAgZAEAQc+D4Pg+D4PggCAIH/ygIAgCAYfB8HwfB8EHP/E+CAIHwfB8H//B///4Pg+D7///5QEAf/4Pn//wQBA//gg//+D4Pv///qAg+CAP+oCD//6gIOeD//8EH///BA+D/4IOf+CD/B9/4IAgZBz/BAHw+5cHwQP/ygPu+D4Pg+D4PuH3Lg//KAu54Pg+D7hw+5cP/+CD/4cOHDhw4f/gg//4Pn//+D4Pv8oD//5cPu//BA//+D///lw+/qAg/+UD7v/+D7+XD7/wffBA//qA+58H3/B9/lgfD7vg+D4Pu/y4Pv6gf/lg+//lAQP/6gPu/+D4Pv/lw+/wQf/1Aff/KAu7/wfB9/4IPg+/8EHwff+XB9/5QH3fB8Hwfd/lg+/6gPu+D4Pg+/8uD7/wQfB9/5cH3/KAu7/wfB8H3/lg+/qA+74Pg+D7/y4Pv/BA+D7/y4Pv/LA+H3fB8H3/lw+/8EH/+XD7v/LB9/UB934Pg+D7/y4ff/BB/8oH3f/lw+7qA+78HwfB9/5cP/+CD/5cPu/8sH3dQH3fB8Hwff+XD7/wQP/lw+7/y4fd/UB93wfB8H3/lw+/8EH/y4fd/5YPu6gPu/B8Hwff+XD//gg/+XD7v/LB93UB9wAAAAA//tQxBQAAADSAAAAAAAAANIAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v/////////////////////////////////////////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

export default function AdminHeader({ onMenuClick, title }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [pendingBookings, setPendingBookings] = useState(0);
  const [signedContracts, setSignedContracts] = useState(0);
  const [newBookingAlert, setNewBookingAlert] = useState(null);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const prevPendingBookingsRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }, []);

  // Fetch unread contacts and pending bookings counts
  useEffect(() => {
    if (!db) return;

    // Listen for unread contacts
    const contactsQuery = query(
      collection(db, 'contacts'),
      where('read', '==', false)
    );
    const unsubContacts = onSnapshot(contactsQuery, (snapshot) => {
      setUnreadContacts(snapshot.size);
    }, (error) => {
      console.error('Error fetching contacts:', error);
    });

    // Listen for pending bookings with latest booking info
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('status', '==', 'pending')
    );
    const unsubBookings = onSnapshot(bookingsQuery, (snapshot) => {
      const newCount = snapshot.size;

      // Check if new booking was added (not on initial load)
      if (!isInitialLoadRef.current && newCount > prevPendingBookingsRef.current) {
        // Get the newest booking
        const newestBooking = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB - dateA;
          })[0];

        if (newestBooking) {
          // Play sound and show alert
          playNotificationSound();
          setNewBookingAlert({
            name: newestBooking.contactName || newestBooking.name,
            artist: newestBooking.artistName || 'Artist',
            date: newestBooking.eventDate,
          });

          // Auto-dismiss alert after 8 seconds
          setTimeout(() => setNewBookingAlert(null), 8000);
        }
      }

      prevPendingBookingsRef.current = newCount;
      isInitialLoadRef.current = false;
      setPendingBookings(newCount);
    }, (error) => {
      console.error('Error fetching bookings:', error);
    });

    // Listen for signed contracts
    const contractsQuery = query(
      collection(db, 'contracts'),
      where('status', '==', 'signed')
    );
    const unsubContracts = onSnapshot(contractsQuery, (snapshot) => {
      setSignedContracts(snapshot.size);
    }, (error) => {
      console.error('Error fetching contracts:', error);
    });

    return () => {
      unsubContacts();
      unsubBookings();
      unsubContracts();
    };
  }, [playNotificationSound]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalNotifications = unreadContacts + pendingBookings + signedContracts;

  const handleNavigate = (path) => {
    navigate(path);
    setShowNotifications(false);
  };

  return (
    <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-gray-800">
      {/* New Booking Alert Popup */}
      {newBookingAlert && (
        <div className="fixed top-4 right-4 z-50 animate-pulse">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-lg shadow-2xl max-w-sm">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Calendar className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-bold">New Booking!</p>
                <p className="text-sm opacity-90">{newBookingAlert.name} wants to book {newBookingAlert.artist}</p>
                {newBookingAlert.date && (
                  <p className="text-xs opacity-75 mt-1">Event: {newBookingAlert.date}</p>
                )}
              </div>
              <button
                onClick={() => setNewBookingAlert(null)}
                className="text-white/70 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => {
                setNewBookingAlert(null);
                navigate('/admin/bookings');
              }}
              className="mt-3 w-full py-2 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
            >
              View Bookings
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-6 py-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Page title */}
          <h1 className="text-xl md:text-2xl font-bold text-white">{title}</h1>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Search - Desktop only */}
          <div className="hidden lg:block relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Search..."
              className="pl-9 bg-gray-900 border-gray-700 h-9"
            />
          </div>

          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {totalNotifications > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-orange-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1">
                  {totalNotifications > 99 ? '99+' : totalNotifications}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                  <h3 className="font-semibold text-white">Notifications</h3>
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="py-2">
                  {totalNotifications === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No new notifications</p>
                  ) : (
                    <>
                      {unreadContacts > 0 && (
                        <button
                          onClick={() => handleNavigate('/admin/contacts')}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition-colors text-left"
                        >
                          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <MessageSquare className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white">Unread Messages</p>
                            <p className="text-xs text-gray-400">{unreadContacts} new contact{unreadContacts !== 1 ? 's' : ''}</p>
                          </div>
                          <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {unreadContacts}
                          </span>
                        </button>
                      )}

                      {pendingBookings > 0 && (
                        <button
                          onClick={() => handleNavigate('/admin/bookings')}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition-colors text-left"
                        >
                          <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-orange-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white">Pending Bookings</p>
                            <p className="text-xs text-gray-400">{pendingBookings} awaiting review</p>
                          </div>
                          <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {pendingBookings}
                          </span>
                        </button>
                      )}

                      {signedContracts > 0 && (
                        <button
                          onClick={() => handleNavigate('/admin/contracts')}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition-colors text-left"
                        >
                          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white">Signed Contracts</p>
                            <p className="text-xs text-gray-400">{signedContracts} awaiting payment</p>
                          </div>
                          <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {signedContracts}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User avatar - Desktop only */}
          <div className="hidden md:flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
